import { useCallback, useContext } from 'react';

import { AppContext, type Controller, DesignActionTypes } from '@context';
import { saveCustomerBlueprint } from '@external';
import { selectNavigation, setIsSaving, useAppDispatch, useAppSelector } from '@state';
import { useAppToast, useReleaseWatcher } from '../hooks';
import { useKeystoneSession } from '../auth/use-keystone-session';

const REFRESH_DELAY_MS = 2500;

/**
 * A newer build is deployed -> save every design with unsaved changes as a draft, then silently
 * re-authenticate. signIn re-runs the OAuth flow, which mints a fresh token (no re-login when the
 * SSO session is live) and loads the new build, so stale-token errors never surface.
 *
 * Mounted inside AppRootContextProvider rather than the global provider because the designs it has
 * to save live on AppContext. The work always lands as a DRAFT -- never promoted to a numbered
 * version, since that is the question the Save dialog asks and an update does not get to answer it --
 * and if any save fails the refresh is abandoned, because reloading on top of work that is still only
 * in memory is the thing this exists to prevent.
 */
export const ReleaseRefreshGuard = (): null => {
	const { status, getToken, signIn } = useKeystoneSession();
	const toast = useAppToast();
	const { appState: { customerDesigns: designs }, appDispatch } = useContext(AppContext);
	const appNav = useAppSelector(selectNavigation);
	const dispatch = useAppDispatch();

	const saveThenRefresh = useCallback(async (): Promise<void> => {
		// kitchen.isLocal is the canonical unsaved-changes flag; isDirty mirrors it.
		const unsaved = designs.designs.filter(({ bpController, isDirty }) => Boolean(bpController) && (isDirty || Boolean(bpController?.kitchen?.isLocal)));

		if (unsaved.length === 0) {
			toast.info({ title: 'A new version is available', description: 'Refreshing to update...', persistent: true });
			window.setTimeout(() => signIn(), REFRESH_DELAY_MS);
			return;
		}

		if (!appNav.email) {
			toast.warning({
				title: 'Update ready — save before refreshing',
				description: 'There is no customer in context to save against. Save your design, then reload to pick up the new version.',
				persistent: true
			});
			return;
		}

		const saving = toast.loading({ title: 'A new version is available', description: 'Saving your work before updating...' });
		dispatch(setIsSaving(true));

		const errors = await Promise.all(unsaved.map(async ({ blueprint, bpController }): Promise<string | null> => {
			if (!bpController) return 'No controller';

			// Always goes up as a DRAFT: updateCustomerBlueprintOnBlob purges existing drafts and stores any
			// version === null payload, so that is the one shape it always accepts. A kitchen already on a
			// numbered version is cloned into a draft of itself (fresh id, baseVersion = that version)
			// rather than written back over what was published.
			const { kitchen } = bpController;
			const kitchenDataOverride = kitchen.version === null ? undefined : kitchen.clone(true).serialize();

			const [, error] = await saveCustomerBlueprint({
				controller: bpController as unknown as Controller,
				customerEmail: appNav.email as string,
				mode: 'live',
				getToken,
				kitchenDataOverride
			});
			if (error) return error;

			bpController.kitchen.isLocal = false;
			appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: blueprint.id, isDirty: false }});
			return null;
		}));

		if (errors.some(Boolean)) {
			dispatch(setIsSaving(false));
			saving.error({
				title: 'Could not save before updating',
				description: 'Your work is still here. Save the design yourself, then reload to pick up the new version.',
				persistent: true
			});
			return;
		}

		saving.success({ title: 'Saved as a draft', description: 'Refreshing to update...' });
		window.setTimeout(() => signIn(), REFRESH_DELAY_MS);
	}, [designs, appNav.email, toast, dispatch, appDispatch, getToken, signIn]);

	const handleNewRelease = useCallback((): void => { void saveThenRefresh(); }, [saveThenRefresh]);

	useReleaseWatcher({ enabled: status === 'ready', onNewRelease: handleNewRelease });

	return null;
};
